// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — grip node schemas (DYN-4 Phase 4b / Fix 7)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD 2018+ encodes dynamic-block grips as separate OBJECTS entities
// whose class names end with `GRIP`:
//
//   BLOCKLINEARGRIP             — pair with a linear / linear-constraint
//                                 parameter; exposes two 1D handles.
//   BLOCKVISIBILITYGRIP         — triangle grip that opens the visibility
//                                 state menu; pairs with a visibility param.
//   BLOCKFLIPGRIP               — flip arrow grip (pairs with flip param).
//   BLOCKROTATIONGRIP           — rotation handle (pairs with rotation).
//   BLOCKALIGNMENTGRIP          — rotate-to-match-geometry handle.
//   BLOCKPROPERTIESTABLEGRIP    — the "↓" grip that opens the block
//                                 properties table dropdown in-place.
//   BLOCKUSERPARAMETERGRIP      — hypothetical; not observed in the wild
//                                 (AutoCAD authors user parameters via the
//                                 Properties Palette only).
//
// Real-world fixture: АБИ1.dxf ibeam ships 5 distinct grips (3 linear, 1
// visibility, 1 properties-table). Every grip carries a group-330 pointing
// at its backing parameter (resolved after the AcDbBlockGrip subclass
// marker) and — for most kinds — a 1010/1020/1030 position in block space.
//
// BLOCKGRIPLOCATIONCOMPONENT is NOT a grip itself: it is the expression
// sub-component AutoCAD uses to derive the grip's live coordinates from
// the evaluation graph (e.g. UpdatedEndX / UpdatedEndY). Phase 4b keeps
// those records in `futureExtensions.rawObjects` so the round-trip
// stays byte-identical without trying to reconstruct the expression AST.
//
// ACDB_DYNAMICBLOCKPROXYNODE is likewise preserved as-is — it is the
// footer node every authored block emits; we do not decode its payload.
//
// Schema intent:
//   - `kind` is a closed enum so consumers (UI, evaluator, round-trip
//     writer) can switch exhaustively.
//   - `parameterHandle` keeps the raw DXF handle string; the bridge layer
//     resolves it to the backing parameter id when the owning metadata is
//     fully built (parameters may not have been parsed yet when we visit
//     the grip).
//   - `position` is optional because BLOCKGRIPLOCATIONCOMPONENT-driven
//     grips sometimes defer their coordinates to the expression graph and
//     omit the 1010/1020 codes on the grip record itself.
//   - `rawGroups` is the full untouched list so `writeGrip` can fall back
//     to verbatim passthrough when the schema-driven emission cannot
//     reconstitute every vendor-specific code faithfully. Main goal of
//     Phase 4b is fidelity, not UX rendering.

import { z } from "zod";

import type { DxfGroup } from "@dxf-ai-editor/dxf-io";

// AutoCAD grip classes observed in the wild. `user` is reserved for
// future expansion (e.g. a user parameter that authors a grip via the
// Block Editor's "Show Grip" toggle combined with a custom property).
export const DYNAMIC_GRIP_KINDS = [
  "linear",
  "visibility",
  "flip",
  "rotation",
  "alignment",
  "properties-table",
  "user",
] as const;

export const dynamicGripSchema = z.object({
  /** DXF handle (group 5) of the grip OBJECT. */
  id: z.string().min(1),
  kind: z.enum(DYNAMIC_GRIP_KINDS),
  /**
   * Handle of the owning evaluation graph / block record (group 330 at
   * the TOP of the record, before the AcDbBlockGrip subclass marker).
   * AutoCAD uses this for its eval-graph traversal; we preserve it so
   * the writer can re-emit the first 330 in the correct slot.
   */
  ownerHandle: z.string().default(""),
  /**
   * Handle of the backing parameter (first group 330 AFTER the
   * `AcDbBlockGrip` subclass marker, if present). Empty string when the
   * grip has no direct parameter association (e.g. the properties-table
   * grip, which binds to the BlockPropertiesTable itself rather than a
   * parameter).
   */
  parameterHandle: z.string().default(""),
  /**
   * Grip position in block-definition space (codes 1010 / 1020 / 1030).
   * Omitted when the DXF record defers the coordinates to a paired
   * BLOCKGRIPLOCATIONCOMPONENT expression.
   */
  position: z
    .object({
      x: z.number(),
      y: z.number(),
      z: z.number().default(0),
    })
    .optional(),
  /**
   * Every DXF group preserved verbatim (including 0/100 markers). The
   * writer falls back to this list when a schema-driven emission would
   * lose vendor-specific codes. Empty by default so consumers that
   * construct a DynamicGrip programmatically (tests / command engine)
   * do not need to fabricate raw groups.
   */
  rawGroups: z
    .array(
      z.object({
        code: z.number(),
        value: z.string(),
      }),
    )
    .default([]),
});
export type DynamicGrip = z.infer<typeof dynamicGripSchema>;

/**
 * Narrow assertion: `rawGroups` field types match the shared DxfGroup
 * shape so writers can splice/spread without coercion. Equivalent at the
 * type level because both are `{ code: number; value: string }`.
 */
export type DynamicGripRawGroup = DxfGroup;
